import React from 'react';
import classNames from 'classnames';
import { Tooltip } from 'reactstrap';
import { CustomerWarehouseInventory } from '../modules/mil-buybox';
import {
    getCalendarBasedGroups,
    getInventoryByTransportDayGroup,
    InventoryDisplayMethod,
    parseReceiptCalendar,
    ReceiptCalendarMap,
    WarehouseTransportGroup
} from '../modules/mil-buybox/utilities';
import { InventoryAvailabilitySearchCriteria, ProductAvailableQuantity } from '@msdyn365-commerce/retail-proxy';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCustomerWarehouses_V2Async, getStockHoldStatusAsync } from '../actions/DataActionExtension.g';
import { DeliveryMode, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { IGetStockHoldStatusResponse } from '../actions/DataServiceEntities.g';
import { displayedInventoryMethod } from '../modules/mil-quick-order/mil-quick-order.props.autogenerated';

interface WarehouseInventoryDisplayProps {
    resources: {
        availableStockForDeliveryLabel: string;
        availableStockForPickupLabel: string;
    };
    state: {
        cutoffTime?: number;
        receiptCalendar?: string;
        custWhInvAvail?: CustomerWarehouseInventory[];
        totalInventoryAvail?: number;
    };
    config: {
        inventoryDisplayMethod?: keyof typeof InventoryDisplayMethod | string;
        sameDayPickupLabel?: string;
        nextDayDeliveryLabel?: string;
        multiDayDeliveryLabel?: string;
    };
    itemId: string;
    isQuickView?: boolean;
    isAuthenticated: boolean;
}

interface InventoryClickIconProps {
    label: string;
    warehouseMembers: CustomerWarehouseInventory[];
    itemId: string;
    isQuickView?: boolean;
}

export interface UseWarehouseInventoryResult {
    custWhInvAvail: CustomerWarehouseInventory[];
    cutoffTime: number | undefined;
    receiptCalendar: string | undefined;
    totalInventoryAvail: number;
    productInventoryInfo: IProductInventoryInformation | undefined;
    stockHoldStatusData: IGetStockHoldStatusResponse | undefined;
}

export const WarehouseInventoryDisplay: React.FC<WarehouseInventoryDisplayProps> = ({
    resources,
    state,
    config,
    itemId,
    isQuickView,
    isAuthenticated
}) => {
    const { availableStockForDeliveryLabel, availableStockForPickupLabel } = resources;
    const { cutoffTime, receiptCalendar, custWhInvAvail = [], totalInventoryAvail = 0 } = state;
    const warehouses = custWhInvAvail;

    const invenDisplayMethod: keyof typeof InventoryDisplayMethod =
        config.inventoryDisplayMethod && config.inventoryDisplayMethod in InventoryDisplayMethod
            ? (config.inventoryDisplayMethod as keyof typeof InventoryDisplayMethod)
            : 'default';

    const sameDayPickupLabel = config.sameDayPickupLabel ?? 'Same Day Pickup';
    const nextDayDeliveryLabel = config.nextDayDeliveryLabel ?? 'Next Day Delivery';
    const multiDayDeliveryLabel = config.multiDayDeliveryLabel ?? '2+ Day Delivery';
    const rcRaw = (receiptCalendar || '').toUpperCase();

    const receiptCalMap = (receiptCalendar ? parseReceiptCalendar(receiptCalendar) : {}) as ReceiptCalendarMap;
    const allWeekdays = receiptCalMap && (Object.values(receiptCalMap) as number[]).every(v => v === 1);
    const shouldShowReceiptCal = !allWeekdays && !rcRaw.includes('STD') && !rcRaw.includes('AFTER') && rcRaw !== '';

    let groups: WarehouseTransportGroup[] = [];

    if (shouldShowReceiptCal) {
        groups = getCalendarBasedGroups(warehouses, receiptCalMap, cutoffTime!, sameDayPickupLabel);
    } else {
        groups = getInventoryByTransportDayGroup(warehouses, cutoffTime!, sameDayPickupLabel, nextDayDeliveryLabel, multiDayDeliveryLabel);
    }

    return (
        <>
            {invenDisplayMethod === InventoryDisplayMethod.byEstDelivery && isAuthenticated && (
                <div className='ms-buybox__inventory-list-container'>
                    <div
                        className={classNames('ms-buybox__inventory-list-container--label', {
                            warehouses: warehouses.length > 0
                        })}
                    >
                        {availableStockForPickupLabel}{' '}
                        <span className='ms-buybox__inventory-list-container--quantity'>
                            {warehouses.find(wh => wh.warehouseCode === wh.CustomerWarehouse)?.inventory.toLocaleString()}
                        </span>
                    </div>
                    {warehouses.length > 0 && invenDisplayMethod === InventoryDisplayMethod.byEstDelivery && groups[0] && (
                        <div key={groups[0].label} className='ms-buybox__inventory-list-container--row'>
                            {`${groups[0].label}: ${groups[0].total.toLocaleString()}`}{' '}
                            {groups[0].total > 0 && (
                                <InventoryClickIcon
                                    label={groups[0].label}
                                    warehouseMembers={groups[0].warehouseMembers}
                                    itemId={itemId}
                                    isQuickView={isQuickView}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
            <div className='ms-buybox__inventory-list-container'>
                <div
                    className={classNames('ms-buybox__inventory-list-container--label', {
                        warehouses: warehouses.length > 0 && invenDisplayMethod !== InventoryDisplayMethod.default
                    })}
                >
                    {availableStockForDeliveryLabel}{' '}
                    <span className='ms-buybox__inventory-list-container--quantity'>{totalInventoryAvail.toLocaleString()}</span>
                </div>

                {warehouses.length > 0 &&
                    invenDisplayMethod === InventoryDisplayMethod.byWarehouse &&
                    [...warehouses]
                        .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName))
                        .map(whi => (
                            <div key={whi.warehouseCode} className='ms-buybox__inventory-list-container--row'>
                                {`${whi.warehouseName}: ${whi.inventory.toLocaleString()}`}
                            </div>
                        ))}

                {warehouses.length > 0 &&
                    invenDisplayMethod === InventoryDisplayMethod.byEstDelivery &&
                    groups.slice(1).map(({ label, total, warehouseMembers }) => (
                        <div key={label} className='ms-buybox__inventory-list-container--row'>
                            {`${label}: ${total.toLocaleString()}`}{' '}
                            {total > 0 && (
                                <InventoryClickIcon
                                    label={label}
                                    warehouseMembers={warehouseMembers}
                                    itemId={itemId}
                                    isQuickView={isQuickView}
                                />
                            )}
                        </div>
                    ))}
            </div>
        </>
    );
};

export const InventoryClickIcon: React.FC<InventoryClickIconProps> = ({ label, warehouseMembers, itemId, isQuickView = false }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const safeLabel = label
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '');
    const uniqueId = `Tooltip-${itemId}-${safeLabel}-${warehouseMembers[0]?.warehouseCode}`;

    const toggleTooltip = () => {
        setIsOpen(prev => !prev);
    };

    return (
        <div className='ms-buybox__inventory-list-container--row--tooltip'>
            <span id={uniqueId} onClick={toggleTooltip} data-toggle='tooltip' style={{ cursor: 'pointer' }}>
                <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 30 30'>
                    <path d='M15,3C8.373,3,3,8.373,3,15c0,6.627,5.373,12,12,12s12-5.373,12-12C27,8.373,21.627,3,15,3z M16,21h-2v-7h2V21z M15,11.5 c-0.828,0-1.5-0.672-1.5-1.5s0.672-1.5,1.5-1.5s1.5,0.672,1.5,1.5S15.828,11.5,15,11.5z' />
                </svg>
            </span>

            <Tooltip
                isOpen={isOpen}
                target={uniqueId}
                toggle={toggleTooltip}
                placement='right'
                container={isQuickView ? '.ms-quickView__body' : undefined}
                className='tooltip-open'
            >
                <div className='ms-buybox__inventory-list-container--row--tooltip--warehouse-group'>
                    {warehouseMembers.map(w => (
                        <div key={w.warehouseCode} className='ms-buybox__inventory-list-container--row--tooltip--warehouse-group--item'>
                            {w.warehouseName}: {w.inventory.toLocaleString()}
                        </div>
                    ))}
                </div>
            </Tooltip>
        </div>
    );
};

export function useWarehouseInventory(
    context: any,
    productRecId: number | undefined,
    quantity: number,
    availableStockLabelText: string,
    outOfStockText: string
): UseWarehouseInventoryResult {
    const [custWhInvAvail, setCustWhInvAvail] = React.useState<CustomerWarehouseInventory[]>([]);
    const [cutoffTime, setCutoffTime] = React.useState<number | undefined>(undefined);
    const [receiptCalendar, setReceiptCalendar] = React.useState<string | undefined>(undefined);
    const [totalInventoryAvail, setTotalInventoryAvail] = React.useState<number>(0);
    const [productInventoryInfo, setProductInventoryInfo] = React.useState<IProductInventoryInformation | undefined>(undefined);
    const [stockHoldStatusData, setStockHoldStatusData] = React.useState<IGetStockHoldStatusResponse | undefined>(undefined);
    const inventoryMethod = context.app.config.displayedInventoryMethod;

    React.useEffect(() => {
        if (!productRecId) {
            setCustWhInvAvail([]);
            setCutoffTime(undefined);
            setReceiptCalendar(undefined);
            setTotalInventoryAvail(0);
            setProductInventoryInfo(undefined);
            setStockHoldStatusData(undefined);
            return;
        }

        (async () => {
            const inventSearch: InventoryAvailabilitySearchCriteria = {
                ProductIds: [productRecId],
                DefaultWarehouseOnly: false,
                FilterByChannelFulfillmentGroup: true
            };

            const availabilitiesPromise = getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, inventSearch);
            const custAcctNum = context.request.user.customerAccountNumber || '';
            const customerWarehousesPromise = getCustomerWarehouses_V2Async({ callerContext: context.actionContext }, custAcctNum);
            const itemId = productRecId.toString();
            const stockHoldStatusesPromise = getStockHoldStatusAsync({ callerContext: context.actionContext }, custAcctNum, itemId);

            // Wait for all to resolve:
            const [availRes, custWhRes, stockHoldRes] = await Promise.all([
                availabilitiesPromise,
                customerWarehousesPromise,
                stockHoldStatusesPromise
            ]);

            const availInvRes = availRes?.ProductWarehouseInventoryAvailabilities ?? [];
            const availableWarehouses = custWhRes?.AvailableWarehouses ?? [];
            const newCustInv: CustomerWarehouseInventory[] = [];

            // const inventoryMethod = 'totalAvailable' as displayedInventoryMethod;

            if (availableWarehouses && availInvRes) {
                availableWarehouses.forEach(wh => {
                    const inventoryInfo = availInvRes.find(inv => inv.InventLocationId === wh.AvailableWarehouse);
                    if (inventoryInfo) {
                        newCustInv.push({
                            warehouseCode: wh.AvailableWarehouse || '',
                            warehouseName: wh.WarehouseName || '',
                            inventory:
                                inventoryMethod === displayedInventoryMethod.physicalAvailable
                                    ? inventoryInfo.PhysicalAvailable || 0
                                    : inventoryInfo.TotalAvailable || 0,
                            transportDays: wh.TransportDays || 0,
                            CustomerWarehouse: wh.CustomerWarehouse || ''
                        });
                    }
                });
            }
            setCustWhInvAvail(newCustInv);

            const aggProductInvAvail = availRes?.AggregatedProductInventoryAvailabilities ?? [];
            let totalInv: number = 0;
            if (newCustInv.length > 0) {
                totalInv = newCustInv.map(w => (w.inventory >= 0 ? w.inventory : 0)).reduce((acc, curr) => acc + curr, 0);
            } else if (aggProductInvAvail.length > 0) {
                if (inventoryMethod === displayedInventoryMethod.physicalAvailable) {
                    totalInv = aggProductInvAvail[0].PhysicalAvailableQuantity ?? 0;
                } else {
                    totalInv = aggProductInvAvail[0].TotalAvailableQuantity ?? 0;
                }
            }
            setTotalInventoryAvail(totalInv);

            const baseInventoryRequired = quantity > 0 ? quantity : 1;
            const availableQuantity = totalInv >= baseInventoryRequired ? totalInv : 0;

            const productAvailQuant: ProductAvailableQuantity = {
                ProductId: productRecId,
                AvailableQuantity: availableQuantity,
                UnitOfMeasure: availInvRes && availInvRes.length > 0 ? availInvRes[0].UnitOfMeasure : undefined
            };

            const newProductInventoryInfo: IProductInventoryInformation = {
                ProductAvailableQuantity: productAvailQuant,
                StockLevelLabel: totalInv >= baseInventoryRequired ? availableStockLabelText : outOfStockText,
                IsProductAvailable: totalInv >= baseInventoryRequired,
                InventLocationId: availInvRes && availInvRes.length > 0 ? availInvRes[0].InventLocationId : undefined,
                deliveryType: DeliveryMode.shipping
            };
            setProductInventoryInfo(newProductInventoryInfo);

            setStockHoldStatusData(stockHoldRes);

            setCutoffTime(custWhRes.DefaultCutoffTime);
            setReceiptCalendar(custWhRes.ReceiptCalendar);
        })();
    }, [context, productRecId, quantity]);

    return {
        custWhInvAvail,
        cutoffTime,
        receiptCalendar,
        totalInventoryAvail,
        productInventoryInfo,
        stockHoldStatusData
    };
}
